import React, { useEffect, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReactTable from 'components/ReactTable';
import { getWhdBulkUploadsByObject, showAddWhsDocumentsModal, hideAddWhsDocumentsModal, submitAddWhsDocuments, showUndoWhdUploadModal, hideUndoWhdUploadModal, undoWhdUpload } from 'actions';

import AddWhsDocumentsModal from 'components/Modals/AddWhsDocumentsModal';
import ConfirmModal from 'components/Modals/ConfirmModal';

import withContainerErrorBoundary from 'components/ErrorBoundaries/ContainerErrorBoundary/withContainerErrorBoundary';
import statementUploadsTabColumnDefs from './statementUploadsTabColumnDefs';
import uploadsTabActions from './statementUploadsTabActions';

const StatementUploadsTab = (props) => {
  const { dispatch, permissions, accountId, gridTotalLimit,
    bulkUploads, bulkUploadCount, isAddWhsDocumentsModalShown,
    isUndoWhdUploadModalShown, isUndoingWhdUpload, undoWhdUploadOriginalFileName } = props;
  const [loading, setLoading] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [gridCriteria, setGridCriteria] = useState({
    columnDefs: statementUploadsTabColumnDefs().concat([]),
    pageIndex: 0,
    sortBy: [],
    filters: [],
    pageCount: 0,
    pageSize: 50,
    itemCount: 0,
    totalLimit: gridTotalLimit,
    selectedItems: [],
    selectedItem: null
  });
  const loadingTime = 500;

  const generateRequest = () => {
    let sortDirection;
    if (gridCriteria.sortBy != null && gridCriteria.sortBy[0]) {
      if (gridCriteria.sortBy[0]?.desc != null) {
        sortDirection = gridCriteria.sortBy[0]?.desc ? 'desc' : 'asc';
      }
    } else {
      sortDirection = null;
    }

    return {
      pageNumber: gridCriteria.pageIndex + 1,
      pageSize: gridCriteria.pageSize,
      totalLimit: gridCriteria.totalLimit,
      sortByField: gridCriteria.sortBy != null ? gridCriteria.sortBy[0]?.id : null,
      sortDirection,
      filterList: gridCriteria.filters,
      accountId
    };
  };

  const sendDataRequest = () => {
    const request = generateRequest();
    dispatch(getWhdBulkUploadsByObject(request));
  };

  const refreshButtonClicked = () => {
    setLoading(true);
    sendDataRequest();
  };

  const hideLoadingAnimation = () => {
    const { isGettingBulkUploads } = props;
    const loadingInterval = setInterval(() => {
      if (!isGettingBulkUploads) {
        setLoading(false);
        clearInterval(loadingInterval);
      }
    }, loadingTime);
  };

  const processData = (incomingDocuments, incomingDocumentsCount) => {
    const nextData = [];
    if (incomingDocuments != null) {
      incomingDocuments.forEach((document) => {
        const datum = {
          id: document.id,
          documentUploadStatus: document.documentUploadStatus || 'N/A',
          documentUploadDate: document.documentUploadDate,
          documentUploadDateDisplay: document.documentUploadDate != null ? moment(document.documentUploadDate).format('DD MMM YYYY') : 'N/A',
          uploadedBy: document.uploadedBy || 'N/A',
          originalFileName: document.originalFileName || 'N/A',
          fileCount: document.fileCount || 0,
          formCount: document.formCount || 0,
          supportingDocumentCount: document.supportingDocumentCount || 0,
          accountId,
          hasFormReviewBegan: document.hasFormReviewBegan || false
        };
        nextData.push(datum);
      });
      const pageCount = Math.ceil(incomingDocumentsCount / gridCriteria.pageSize);
      setData(nextData);
      setGridCriteria(prev => ({
        ...prev,
        itemCount: incomingDocumentsCount,
        pageCount,
        totalLimit: gridTotalLimit
      }));
    }
  };

  useEffect(() => {
    if (bulkUploads && bulkUploadCount >= 0) {
      processData(bulkUploads, bulkUploadCount);
      hideLoadingAnimation();
      if (!initialLoaded) {
        setInitialLoaded(true);
      }
    }
  }, [bulkUploads]);

  useEffect(() => {
    sendDataRequest();
  }, []);

  const onGridCriteriaChanged = (newGridCriteria) => {
    setGridCriteria({ ...newGridCriteria });
  };

  useEffect(() => {
    sendDataRequest();
  }, [gridCriteria.pageIndex, gridCriteria.sortBy, gridCriteria.filters, gridCriteria.pageSize]);

  const addWhsDocumentAction = (uploadedDocs) => {
    dispatch(submitAddWhsDocuments(accountId, uploadedDocs));
  };

  const openAddDocumentModal = () => {
    dispatch(showAddWhsDocumentsModal());
  };

  const closeAddDocumentModal = () => {
    dispatch(hideAddWhsDocumentsModal());
  };

  useEffect(() => {
    if (isAddWhsDocumentsModalShown === false) {
      sendDataRequest();
    }
  }, [isAddWhsDocumentsModalShown]);

  const openUndoUploadModal = (upload) => {
    if (!upload) {
      return;
    }

    dispatch(showUndoWhdUploadModal(upload.id, upload.originalFileName));
  };

  const closeUndoUploadModal = () => {
    dispatch(hideUndoWhdUploadModal());
  };

  const confirmUndoUpload = () => {
    const { undoWhdUploadId } = props;

    if (!undoWhdUploadId) {
      closeUndoUploadModal();
      return;
    }

    dispatch(undoWhdUpload(accountId, undoWhdUploadId, () => {
      sendDataRequest();
    }));
  };

  const onRowSelectionChanged = (rowSelections) => {
    setGridCriteria({ ...gridCriteria, ...rowSelections });
  };

  const getActionsColumn = () => uploadsTabActions({ permissions, dispatch, openUndoUploadModal });

  return (
    <div className="landing-page documents-tab-content">
      <div className="panel">
        <div className="panel-body results form-results">
          <div className="table-header">
            <div className="quick-filters">
              <span>
                { /* No quick filters yet. */ }
              </span>
            </div>
            <div className="right-align-actions">
              <span className="supporting-documents-note-label">
                Note: Files that are not identified as forms are saved as Supporting Documents on the account and will not appear on the grid below.
              </span>
              <span className="refresh-button">
                <button
                  type="button"
                  className="btn btn-secondary"
                  title="Refresh Grid"
                  id="refresh-grid-button"
                  onClick={ () => refreshButtonClicked() }
                >
                  <span className="icon icon-refresh" />
                </button>
              </span>
              <span className="add-button">
                <button
                  type="button"
                  className="btn btn-secondary"
                  title="Add Documents"
                  id="add-documents-button"
                  onClick={ () => openAddDocumentModal() }
                >
                  Add Documents
                </button>
              </span>
            </div>
          </div>
          <div className="table">
            <ReactTable
              data={ data }
              gridCriteria={ gridCriteria }
              onGridCriteriaChanged={ onGridCriteriaChanged }
              onRowSelectionChanged={ onRowSelectionChanged }
              actions={ getActionsColumn() }
              loading={ loading }
              initialLoaded={ initialLoaded }
              className="workflow"
            />
          </div>
        </div>
      </div>
      <AddWhsDocumentsModal
        isShown={ isAddWhsDocumentsModalShown }
        onConfirmUpload={ (docs) => { addWhsDocumentAction(docs); } }
        onCancel={ () => closeAddDocumentModal() }
        accountId={ accountId }
      />
      <ConfirmModal
        title="Undo upload"
        message={ `Are you sure you want to undo the upload ${ undoWhdUploadOriginalFileName || '' }?` }
        isShown={ isUndoWhdUploadModalShown }
        isPerformingAction={ isUndoingWhdUpload }
        onConfirm={ () => confirmUndoUpload() }
        onCancel={ () => closeUndoUploadModal() }
        confirmTitle="Yes"
        cancelTitle="No"
      />
    </div>
  );
};

StatementUploadsTab.propTypes = {
  gridTotalLimit: PropTypes.number.isRequired,
  dispatch: PropTypes.func.isRequired,
  accountId: PropTypes.number.isRequired,
  isGettingBulkUploads: PropTypes.bool,
  bulkUploads: PropTypes.arrayOf(PropTypes.shape()),
  bulkUploadCount: PropTypes.number,
  permissions: PropTypes.arrayOf(PropTypes.string),
  isAddWhsDocumentsModalShown: PropTypes.bool,
  isUndoWhdUploadModalShown: PropTypes.bool,
  isUndoingWhdUpload: PropTypes.bool,
  undoWhdUploadId: PropTypes.number,
  undoWhdUploadOriginalFileName: PropTypes.string,
};

function mapStateToProps(state) {
  const {
    auth: {
      permissions,
    },
    forms: {
      gridTotalLimit,
    },
    withholdingStatement: {
      isGettingBulkUploads,
      bulkUploads,
      bulkUploadCount,
      isAddWhsDocumentsModalShown,
      isUndoWhdUploadModalShown,
      isUndoingWhdUpload,
      undoWhdUploadId,
      undoWhdUploadOriginalFileName,
    }
  } = state;

  return {
    gridTotalLimit,
    isGettingBulkUploads,
    bulkUploads,
    bulkUploadCount,
    permissions,
    isAddWhsDocumentsModalShown,
    isUndoWhdUploadModalShown,
    isUndoingWhdUpload,
    undoWhdUploadId,
    undoWhdUploadOriginalFileName,
  };
}

StatementUploadsTab.defaultProps = {
  isGettingBulkUploads: false,
  bulkUploads: [],
  bulkUploadCount: -1,
  permissions: null,
  isAddWhsDocumentsModalShown: false,
  isUndoWhdUploadModalShown: false,
  isUndoingWhdUpload: false,
  undoWhdUploadId: 0,
  undoWhdUploadOriginalFileName: '',
};

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withContainerErrorBoundary(StatementUploadsTab, 'StatementUploadsTab'));
